import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect} from 'react';
import {BaseFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-fill-color-panel';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import type {ColorStorage} from '@Components/color-picker-v2';
import {
  convertColorPickerColorsToRGBString,
  convertColorsToFabricColor,
  convertRgbaArrayColorsToRGBString,
  getColorPickerColorTypeFromFabricColor,
  getRgbaArrayFromFabricColor,
  convertFabricColorToFillObject,
  getFillTypeFromColorFillType,
  getNewFabricColorForFillType,
} from '@Utils/color.util';
import type {ColorFillPropertyType} from '@Components/color-fill';

interface VectorFillColorPanelProps {
  closePanel?(): void;
  isAlphaDisabled?: boolean;
  isGradientEnabled?: boolean;
}

export function VectorFillColorPanel({closePanel = (): void => {}, ...props}: VectorFillColorPanelProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const fill = useGetKeyFromActiveItem('fill');

  if (!activeItem || (activeItem && !('fill' in activeItem)) || fill === undefined) {
    throw new Error(`Item doesn't have fill property`);
  }

  const currentColorType = useMemo(() => {
    return getColorPickerColorTypeFromFabricColor(fill);
  }, [fill]);
  const fillObject = useMemo(() => {
    return convertFabricColorToFillObject(fill);
  }, [fill]);

  if (currentColorType === undefined) {
    throw new Error(`Incorrect color:  ${JSON.stringify(fill)}`);
  }

  useEffect((): void => {
    if (fill === null) {
      closePanel();
    }
  }, [fill]);

  const onChangeColorFillColor = (newColorPickerColors: ColorStorage[]): void => {
    void activeItem.updateFromObject(
      {
        fill: convertColorsToFabricColor(convertColorPickerColorsToRGBString(newColorPickerColors), fillObject.fillType),
      },
      {undoable: false}
    );
  };

  const onChangeColorEnded = (newColorPickerColors: ColorStorage[]): void => {
    void activeItem.updateFromObject(
      {
        fill: convertColorsToFabricColor(convertColorPickerColorsToRGBString(newColorPickerColors), fillObject.fillType),
      },
      {undoable: true}
    );
  };

  const onChangeType = (type: ColorFillPropertyType): void => {
    if (type === currentColorType) {
      return;
    }

    void activeItem.updateFromObject(
      {
        fill: getNewFabricColorForFillType(getFillTypeFromColorFillType(type), convertRgbaArrayColorsToRGBString(getRgbaArrayFromFabricColor(fill))),
      },
      {undoable: true}
    );
  };

  return (
    <BaseFillColorPanel
      colorObject={fillObject}
      onChangeColor={onChangeColorFillColor}
      onChangeColorEnded={onChangeColorEnded}
      onChangeType={onChangeType}
      isAlphaDisabled={props.isAlphaDisabled}
      isGradientEnabled={props.isGradientEnabled}
    />
  );
}
